import apiService from '@/services/ApiService';
import { ActionContext } from 'vuex';
import { State } from '@/store/state';
import { Article } from '@/models/article/Article';
import { commitSetSelectedArticle } from '@/store/commits/workspaceViewCommits';
import { dispatchLoadCompanyUsers } from '@/store/dispatchers/companyDispatchers';
import { dispatchLoadArticleComments } from '@/store/dispatchers/articleCommentsDispatchers';
import authService from '@/services/AuthService';
import { ArticleEditMode } from '@/@types/ArticleEditMode';
import { wait } from '@/other/wait';
import urlService from '@/services/UrlService';
import { workspaceService } from '@/services/workspace/WorkspaceService';
import router from '@/router';
import { Paragraph } from '@/models/article/Paragraph';
import { commitSetArticleMode } from '@/store/commits/sharedCommits';

export const workspaceViewActions = {
  async workspaceViewSelectFirstArticle({ state, dispatch }: ActionContext<State, {}>) {
    const order = state.workspaceView.workspace?.order;
    const articleItems = state.workspaceView.articles;

    if (order?.length && articleItems?.length) {
      // Find the first non-archived article based on the order
      const findFirstId = (orderItems: any[]): string | null => {
        for (const item of orderItems) {
          const articleSummary = articleItems.find((a) => a.id === item.value);
          if (articleSummary && !articleSummary.isArchived) {
            return item.value;
          }
          if (item.items && item.items.length > 0) {
            const nestedId = findFirstId(item.items);
            if (nestedId) return nestedId;
          }
        }
        return null;
      };

      const firstArticleId = findFirstId(order);
      if (firstArticleId) {
        // Use the openSelectedArticle action to load it and navigate
        await dispatch('openSelectedArticle', { articleId: firstArticleId });
      }
    }
  },

  async workspaceViewPageMounted({ state }: ActionContext<State, {}>) {
    const companyUsers = state.companyUsers;

    if (!companyUsers.length && authService.isAuthorized()) {
      await dispatchLoadCompanyUsers();
    }
  },

  async workspaceViewWorkspaceLoaded({ dispatch }: ActionContext<State, {}>) {
    await dispatch('commentsSidebarCheckAutoShowComment');
  },

  async workspaceViewSelectedArticleChanged({ state }: ActionContext<State, any>) {
    const selectedArticle = state.workspaceView.selectedArticle;
    if (!selectedArticle) return;
    if (selectedArticle?.id && authService.isAuthorized()) {
      await dispatchLoadArticleComments();
    }

    const mode = state.articleMode;
    if (mode === ArticleEditMode.NOTES) {
      if (!selectedArticle?.hasText() && !selectedArticle?.hasPinnedNote() && selectedArticle.isVideoArticle()) {
        commitSetArticleMode(ArticleEditMode.VIDEO);
      }
    }
  },

  async openSelectedArticle(
    { state }: ActionContext<State, {}>,
    { articleId, newTab }: { articleId: string; newTab?: boolean }
  ) {
    const workspaceId = state.workspaceView.workspace?.id;
    if (!workspaceId) {
      return;
    }

    const { data } = await apiService.workspace.getArticle(workspaceId, articleId);
    const article = Article.fromJson(data.article);

    if (!state.workspaceView.isSharedMode) {
      const mode = article.pinnedNote
        ? ArticleEditMode.NOTES
        : article.isVideoArticle() || !article.isTextArticle()
        ? ArticleEditMode.VIDEO
        : ArticleEditMode.NOTES;

      const route = urlService.getArticleViewRoute(article, state.workspaceView.workspace, mode);

      if (newTab) {
        window.open(urlService.getArticleViewUrl(article, state.workspaceView.workspace, mode), '_blank');
      } else {
        commitSetSelectedArticle(article);
        await router.push(route);
        await wait(100);
        workspaceService.playArticle(article, false);
      }
    }
  },

  async openSelectedParagraph(
    { state }: ActionContext<State, {}>,
    { paragraph, article, newTab }: { paragraph: Paragraph; article: Article; newTab?: boolean }
  ) {
    const isAnotherArticle = article.id !== state.workspaceView.selectedArticle.id;
    if (isAnotherArticle && !newTab) commitSetSelectedArticle(article);

    if (!state.workspaceView.isSharedMode) {
      if (newTab) {
        // Open the link in a new tab
        window.open(
          urlService.getParagraphViewUrl(paragraph.getSlug(), article, state.workspaceView.workspace),
          '_blank'
        );
        await wait(100);
      } else {
        await router.push(
          urlService.getParagraphViewRoute(paragraph.getSlug(), article, state.workspaceView.workspace)
        );
        await wait(100); // wait as route changed and article may contain a few paragraphs with the same name
      }
    }

    !newTab && workspaceService.playParagraph({ paragraph, article }, !isAnotherArticle);
  },
};
